<template>
    <div class="form-group">
        <div id="loader" v-if="isLoading">
            <div class="spinner"></div>
        </div>
        <input type="hidden" name="_token" v-model="formRef.csrf" />
        <h2 v-if="props.sessions && props.sessions.length <= 0" data-test="no-sessions-message">There are no info sessions available to book</h2>
        <div v-else data-test="info-session-form-content">
            <div v-if="props.content?.formattedText" v-html="props.content.formattedText" />
            <h3>Choose your session</h3>
            Select the session you'd like to attend.  Booking will close 24 hours before the session starts.
            <div class="section" data-test="info-session-section">
                <div
                    v-bind:key="session.info_session_id"
                    v-for="session in props.sessions"
                >
                    <div
                        v-if="(Number(session.current_bookings) < Number(session.session_capacity)) && Number(session.info_session_id) != 0"
                        @click="selectSession(session)"
                    >
                        <info-session-book-component
                            :start-date="session.start_date"
                            :start-time="session.start_time"
                            :end-time="session.end_time"
                            :info-session-id="session.info_session_id"
                            :selected="selected"
                        />
                    </div>
                    <div
                        class="pe-none"
                        v-else-if="Number(session.current_bookings) >= Number(session.session_capacity)"
                    >
                        <info-session-fully-booked-component
                            :start-date="session.start_date"
                            :start-time="session.start_time"
                            :end-time="session.end_time"
                        />
                    </div>
                    <div
                        class="pe-none"
                        v-else-if="Number(session.info_session_id) == 0 && (Number(session.current_bookings) < Number(session.session_capacity))"
                    >
                        <info-session-hours
                            :start-date="session.start_date"
                            :start-time="session.start_time"
                            :end-time="session.end_time"
                        />
                    </div>
                </div>
                <span
                    data-test="info-session-error"
                    class="enquiry-errors"
                    v-if="formRef.errors.has('info_session_id')"
                    v-html="formRef.errors.get('info_session_id')"
                    role="alert"
                    aria-live="assertive"
                ></span>
            </div>
        </div>
        <div v-if="props.sessions && props.sessions.length <= 0"></div>
        <div v-else>
            <h3 data-test="contact-section">Your email address</h3>
            <h5>We’ll use this email address to send you:</h5>
          <ul>
              <li>confirmation of your booking</li>
              <li>important information to help you feel prepared for the session</li>
              <li>instructions on how to join the Information Session.</li>
          </ul>
            <label for="email_address"
            class="form-label"
                >Email address <i class="fas fa-asterisk"></i
            ></label>
            <input
                type="text"
                id="email_address"
                class="form-control"
                name="email_address"
                autocomplete="email"
                v-model="formRef.email_address"
                data-test="email-address-field"
                required
            />

            <span
                data-test="email-address-error"
                class="enquiry-errors"
                v-if="formRef.errors.has('email_address')"
                v-html="formRef.errors.get('email_address')"
                role="alert"
                aria-live="assertive"
            >
            </span>
        </div>

        <div v-if="props.sessions && props.sessions.length <= 0"></div>
        <div v-else class="form__buttons form-group py-3">
            <button
                class="btn btn-primary"
                @click.prevent="nextPage()"
                data-test="btn-next"
            >
                Next
            </button>

            <span
                data-test="select-session-error"
                class="enquiry-errors d-block mt-2"
                v-if="Object.entries(formRef.errors.errors).length"
                v-html="formRef.errors.errors.message"
                ></span>
        </div>
    </div>

</template>

<script setup>
import InfoSessionBookComponent from "./InfoSessionBookComponent.vue";
import InfoSessionFullyBookedComponent from "./InfoSessionFullyBookedComponent";
import InfoSessionHours from "./InfoSessionHours.vue"

import Form from "../../../../Form.js";
import { ref, markRaw } from "vue";

const props = defineProps({
    step: Number,
    sessions: Array,
    groupSessionId: String,
    programme: String,
    formData: Object,
    content: Object
})

const emit = defineEmits(['setStep','submitButton'])

let formRef = markRaw(ref(new Form({
    info_session_id: null,
    info_session_start_date: null,
    info_session_start_time: null,
    info_session_end_time: null,
    email_address: null,
    programme: props.programme,
    group_session_id: props.groupSessionId
}, props.formData)))

const selected = ref(null)
const isLoading = ref(false)

function selectSession(info_session) {
    selected.value = info_session.info_session_id;
    formRef.value.info_session_id = info_session.info_session_id;
    formRef.value.info_session_start_date = info_session.start_date;
    formRef.value.info_session_start_time = info_session.start_time;
    formRef.value.info_session_end_time = info_session.end_time;
}

function nextPage() {
    let infosessionChoice = selected.value;
    if (infosessionChoice === null){
        Swal.fire({
            text: 'Please choose a session to attend',
            confirmButtonText: 'OK'
        })
        return;
    }
    isLoading.value = true
    return formRef.value
        .post("/registration/step1")
        .then((response) => {
            let userExists = response.userExists
            let userBooked = response.userBooked

            if (userExists && !userBooked) {
                emit("setStep", {
                    data: {
                        info_session_id: formRef.value.info_session_id,
                        start_date: formRef.value.info_session_start_date,
                        start_time: formRef.value.info_session_start_time,
                        end_time: formRef.value.info_session_end_time,
                        email_address: formRef.value.email_address
                    }})
            } else {
                emit('submitButton', { data: {
                        info_session_id: formRef.value.info_session_id,
                        start_date: formRef.value.info_session_start_date,
                        start_time: formRef.value.info_session_start_time,
                        end_time: formRef.value.info_session_end_time,
                        email_address: formRef.value.email_address,
                        programme: props.programme
                    }});
            }
        })
        .catch(err => {
            if (err.code && err.code === "CURRENTLY_IN_PROGRESS") {
                window.location.href = "/error/application-in-progress";
                return;
            }
            console.log(isLoading)
            formRef.value.errors.record(err);
        })
        .finally(() => (isLoading.value = false));
}
</script>
